/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { ROUTES } from "@nf/constants";
import { captureMessage } from "@sentry/nextjs";
import { ReactElement } from "react";
import { GetServerSideProps } from "next";

import DetailsLayout from "layouts/DetailsLayout";
import MovieDetailsScreen, {
  type MediaDetailsScreenProps,
} from "screens/MovieDetailsScreen";
import { getCountryRoutes } from "@nf/common";

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const setCacheHeader = await import("helpers/cacheHeaderHelper").then(
    (mod) => mod.setCacheHeader
  );
  const { getCommonServerSideProps } = await import(
    "helpers/getCommonServerSideProps"
  );

  const { commonProps, DataProvider } = await getCommonServerSideProps(ctx);
  const countryRoutes = getCountryRoutes(commonProps.country);

  setCacheHeader(ctx.res);

  try {
    const media = await DataProvider.getMedia({
      MediaId: `${ctx.params?.id}`,
      IncludeCategories: true,
      IncludeImages: true,
      IncludePeople: true,
      IncludeRecommendedMedia: false,
      IncludeCollections: false,
      IncludeSeoData: true,
    });

    let extraMaterialsPromise;
    let relatedMaterialsPromise;

    if (media?.ExtraMaterialsIds?.length) {
      extraMaterialsPromise = DataProvider.getMediaExtraMaterials(
        media.ExtraMaterialsIds
      );
    }

    if (media?.RelatedMaterialsIds?.length) {
      relatedMaterialsPromise = DataProvider.getMediaExtraMaterials(
        media.RelatedMaterialsIds
      );
    }

    const [extraMaterials, relatedMaterials] = await Promise.all([
      extraMaterialsPromise,
      relatedMaterialsPromise,
    ]);

    if (commonProps?.isChild && media?.ContentAdvisory) {
      return {
        redirect: {
          destination: `/${commonProps.country}/${commonProps.language}${countryRoutes.KIDS}`,
        },
        props: {},
      };
    }

    return {
      props: {
        ...commonProps,
        media,
        extraMaterials,
        relatedMaterials,
      },
    };
  } catch (e: any) {
    if (e?.Message && e?.Message === "UNKNOWN_ASSET") {
      captureMessage(e.Message, {
        level: "warning",
        tags: {
          MediaId: `${ctx?.params?.id}`,
          error: e,
        },
      });
    } else {
      captureMessage(e, {
        level: "error",
        tags: {
          MediaId: `${ctx?.params?.id}`,
          error: e,
        },
      });
    }

    return {
      redirect: {
        destination: `/${commonProps.country}/${commonProps.language}${ROUTES.PAGE_NOT_FOUND}`,
      },
      props: {
        ...commonProps,
      },
    };
  }
};

const MovieDetailsScreenWrapper = (props: MediaDetailsScreenProps) => {
  return <MovieDetailsScreen {...props} />;
};

MovieDetailsScreenWrapper.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  return <DetailsLayout config={props.config}>{page}</DetailsLayout>;
};

export default MovieDetailsScreenWrapper;
